import type { RefObject } from 'react';

import type { LineChartHandle } from '~/shared/components/LineChart/LineChart';
import type { ExportBatch } from '~/shared/hooks/useExportFetch';
import type { GraphResponse, OptLogVolumeActions, OptLogVolumeActionsDetailsResponse } from '~/shared/types/api';
import type { SelectOption } from '~/shared/utils/types';
import { enrichActionsWithActorDetails } from '~/shared/utils/actionActor';
import {
  downloadTabularExportBatch,
  downloadTabularExportFiles,
  ExportDownloadScheduler,
  generateExportFilename,
  graphDataPointToExportRowCsv,
  graphDataPointToExportRowJson,
  injectDateParams,
  optimizationActionToExportRowCsv,
  optimizationActionToExportRowJson,
} from '~/shared/utils/exportUtils';

export interface PreRenderedGraphPng {
  name: 'Utilization_Chart' | 'Usage_Chart';
  blob: Blob;
}

export interface ReportsExportParams {
  activeFilters: SelectOption[];
  filterSelection: 'with_filters' | 'all_data';
  showFullGraph: boolean;
  dataType: { graphs: boolean; table: boolean };
  graphFormats: Array<'png' | 'csv' | 'json'>;
  tableFormats: Array<'csv' | 'json'>;
  selectedStartDate: string;
  selectedEndDate: string;
  tableQueryUrl: string;
  tableCountUrl: string;
  chartQueryUrl: string;
  preRenderedGraphPngs?: PreRenderedGraphPng[];
  utilizationChartRef: RefObject<LineChartHandle | null>;
  usageChartRef: RefObject<LineChartHandle | null>;
}

interface ReportsExportDeps {
  fetchDataInBatches: (
    dataUrl: string,
    countUrl: string,
    responseKey?: string
  ) => AsyncGenerator<ExportBatch<OptLogVolumeActions>>;
  xfetch: <T = unknown>(url: string) => Promise<T>;
  activeTenantName: string | null | undefined;
  canReadUsers: boolean;
}

export async function executeReportsExport(
  {
    activeFilters,
    filterSelection,
    showFullGraph,
    dataType,
    graphFormats,
    tableFormats,
    selectedStartDate,
    selectedEndDate,
    tableQueryUrl,
    tableCountUrl,
    chartQueryUrl,
    preRenderedGraphPngs,
    utilizationChartRef,
    usageChartRef,
  }: ReportsExportParams,
  { fetchDataInBatches, xfetch, activeTenantName, canReadUsers }: ReportsExportDeps
): Promise<void> {
  const scheduler = new ExportDownloadScheduler();
  const withFilters = filterSelection === 'with_filters';
  const filtersForName = withFilters ? activeFilters : [];
  const today = new Date();
  const accountName = activeTenantName || 'Account';

  if (dataType.graphs) {
    const exportPng = graphFormats.includes('png');
    const exportCsv = graphFormats.includes('csv');
    const exportJson = graphFormats.includes('json');
    const tabularGraphFormats = [
      ...(exportCsv ? (['csv'] as const) : []),
      ...(exportJson ? (['json'] as const) : []),
    ];

    const chartConfigs = [
      { ref: utilizationChartRef, name: 'Utilization_Chart' as const },
      { ref: usageChartRef, name: 'Usage_Chart' as const },
    ];

    if (exportPng && preRenderedGraphPngs?.length) {
      for (const { name, blob } of preRenderedGraphPngs) {
        const filename = generateExportFilename(accountName, name, filtersForName, today);
        await scheduler.download(blob, `${filename}.png`, 'image/png');
      }
    }

    const shouldFetchGraphData =
      tabularGraphFormats.length > 0 || (exportPng && !preRenderedGraphPngs?.length);
    if (shouldFetchGraphData) {
      const exportChartUrl = injectDateParams(chartQueryUrl, selectedStartDate, selectedEndDate);
      const graphResponse = await xfetch<GraphResponse>(exportChartUrl);
      const graphData = graphResponse.utilizationGraph ?? [];

      for (const { ref, name } of chartConfigs) {
        const baseFilename = generateExportFilename(accountName, name, filtersForName, today);

        if (exportPng && !preRenderedGraphPngs?.length) {
          const blob = await ref.current?.exportWithData(graphData);
          if (!blob) throw new Error('Could not export chart image.');
          await scheduler.download(blob, `${baseFilename}.png`, 'image/png');
        }

        if (tabularGraphFormats.length > 0) {
          await downloadTabularExportFiles({
            rows: graphData,
            baseFilename,
            formats: tabularGraphFormats,
            toCsvRow: (item) => graphDataPointToExportRowCsv(item, showFullGraph),
            toJsonRow: (item) => graphDataPointToExportRowJson(item, showFullGraph),
            scheduler,
          });
        }
      }
    }
  }

  if (dataType.table && tableFormats.length > 0) {
    const exportUrl = injectDateParams(tableQueryUrl, selectedStartDate, selectedEndDate);
    const baseFilename = generateExportFilename(
      accountName,
      'Optimization_Actions_Table',
      filtersForName,
      today
    );

    for await (const { part, totalParts, data, batchUrl } of fetchDataInBatches(
      exportUrl,
      tableCountUrl,
      'actions'
    )) {
      const enrichedActions = await enrichActionsWithActorDetails({
        actions: data,
        listQueryUrl: batchUrl,
        canReadUsers,
        fetchDetails: (url) => xfetch<OptLogVolumeActionsDetailsResponse>(url),
      });

      await downloadTabularExportBatch({
        rows: enrichedActions,
        baseFilename,
        formats: tableFormats,
        toCsvRow: optimizationActionToExportRowCsv,
        toJsonRow: optimizationActionToExportRowJson,
        part,
        totalParts,
        scheduler,
      });
    }
  }
}
